/**
 * AIContractImportWizard — Wizard IA « Créer/enrichir un salarié depuis un contrat ».
 *
 * Mode `create` :
 *   Upload → détection → preview éditable → vérif doublon → création employee + contract
 *
 * Mode `enrich` (fiche existante) :
 *   Upload → détection → apply direct (merge non-destructif + nouveau contrat + doc)
 *
 * L'IA (Gemini 3 Flash) extrait : identité, adresse, SS, poste, catégorie,
 * classification, convention, coefficient, salaires, période essai, temps de
 * travail, dates, type de contrat. Tous les champs sont éditables avant validation.
 */
import { useState, useRef, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, Upload, FileText, CheckCircle2, Loader2, AlertTriangle,
  ArrowLeft, ArrowRight, XCircle, UserPlus, UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STEPS = ["upload", "preview", "done"];

const CONTRACT_TYPES = [
  { value: "cdi", label: "CDI" }, { value: "cdd", label: "CDD" },
  { value: "alternance", label: "Alternance" }, { value: "stage", label: "Stage" },
  { value: "interim", label: "Intérim" },
];

const CATEGORIES = [
  { value: "cadre", label: "Cadre" },
  { value: "non_cadre", label: "Non-cadre" },
  { value: "etam", label: "ETAM" },
];

const WORK_TIME = [
  { value: "full_time", label: "Temps plein" },
  { value: "part_time", label: "Temps partiel" },
  { value: "forfait_jours", label: "Forfait jours" },
];

const FAMILY_STATUS = [
  { value: "single", label: "Célibataire" }, { value: "married", label: "Marié·e" },
  { value: "pacs", label: "Pacsé·e" }, { value: "divorced", label: "Divorcé·e" },
  { value: "widowed", label: "Veuf·ve" },
];

function FileDrop({ file, onPick }) {
  const inputRef = useRef(null);
  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) onPick(f);
  };
  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className={cn(
        "relative border-2 border-dashed rounded-xl p-6 cursor-pointer transition",
        "hover:border-blue-400 hover:bg-blue-50/40",
        file ? "border-emerald-400 bg-emerald-50/40" : "border-slate-300 bg-slate-50/40",
      )}
      data-testid="drop-contract"
    >
      <input id="ai-contract-file-input" name="ai-contract-file-input" ref={inputRef} type="file" className="hidden"
        accept="application/pdf,image/png,image/jpeg,image/webp"
        onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])} />
      <div className="flex items-center gap-3">
        {file
          ? <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
          : <Upload className="w-6 h-6 text-slate-400 flex-shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-800">
            {file ? file.name : "Dépose ici le contrat de travail signé"}
          </p>
          <p className="text-xs text-slate-500">
            {file
              ? `${(file.size / 1024).toFixed(0)} Ko · prêt pour l'analyse IA`
              : "PDF, JPG ou PNG — 8 Mo maximum"}
          </p>
        </div>
        {file && (
          <Button type="button" size="sm" variant="ghost"
            onClick={(e) => { e.stopPropagation(); onPick(null); }}
            className="text-slate-500 hover:text-red-600">
            <XCircle className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function FieldRow({ label, children, required }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
        {label}{required && <span className="text-red-600 ml-0.5">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default function AIContractImportWizard({
  open, onOpenChange, onDone, mode = "create", employeeId = null,
}) {
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [empForm, setEmpForm] = useState({});
  const [ctrForm, setCtrForm] = useState({});
  const [duplicateAction, setDuplicateAction] = useState(null); // create_new | merge_into
  const [targetEmployeeId, setTargetEmployeeId] = useState(null);
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  const reset = () => {
    setStep("upload"); setFile(null); setResult(null);
    setEmpForm({}); setCtrForm({});
    setDuplicateAction(null); setTargetEmployeeId(null);
    setDetecting(false); setApplying(false);
  };

  const runDetect = async () => {
    if (!file) { toast.error("Dépose d'abord le contrat de travail."); return; }
    setDetecting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const url = mode === "enrich"
        ? `/hr/employees/${employeeId}/ai-enrich-from-contract`
        : "/hr/employees/ai-import-contract";
      const r = await api.post(url, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Mode enrich = one-shot (backend applique direct)
      if (mode === "enrich") {
        toast.success(r.data?.fields_updated?.length
          ? `Fiche enrichie · ${r.data.fields_updated.length} champ(s) rempli(s)`
          : "Contrat ajouté à la fiche (aucun champ vide à remplir)");
        setStep("done");
        setResult(r.data);
        onDone?.();
        return;
      }
      // Mode create : preview éditable
      setResult(r.data);
      setEmpForm(r.data.employee || {});
      setCtrForm(r.data.contract || {});
      if ((r.data.duplicates || []).length > 0) {
        setTargetEmployeeId(r.data.duplicates[0]?.id || null);
      }
      setStep("preview");
    } catch (e) {
      toast.error(apiError(e, "Détection IA échouée"));
    } finally { setDetecting(false); }
  };

  const applyResult = async () => {
    if (mode !== "create") return;
    if (!empForm.first_name || !empForm.last_name) {
      toast.error("Prénom et nom sont obligatoires."); return;
    }
    setApplying(true);
    try {
      const payload = {
        upload_id: result.upload_id,
        employee: empForm,
        contract: ctrForm,
        attach_document: true,
      };
      const hasDup = (result.duplicates || []).length > 0;
      if (hasDup) {
        payload.duplicate_action = duplicateAction || "create_new";
        if (duplicateAction === "merge_into") {
          payload.target_employee_id = targetEmployeeId;
        }
      }
      const r = await api.post("/hr/employees/ai-import-contract/apply", payload);
      toast.success(r.data?.merged
        ? "Salarié enrichi et nouveau contrat ajouté"
        : "Salarié créé automatiquement depuis le contrat");
      setStep("done");
      setResult((prev) => ({ ...prev, applied: r.data }));
      onDone?.(r.data);
    } catch (e) {
      toast.error(apiError(e, "Création échouée"));
    } finally { setApplying(false); }
  };

  const close = () => { reset(); onOpenChange?.(false); };

  const hasDuplicates = useMemo(
    () => (result?.duplicates || []).length > 0,
    [result],
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
                      data-testid="ai-contract-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-purple-600" />
            {mode === "enrich"
              ? "Enrichir la fiche depuis un contrat"
              : "Créer un salarié depuis son contrat"}
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          <StepPills current={step} enrich={mode === "enrich"} />
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 py-2">
          {step === "upload" && (
            <div className="space-y-4">
              <Alert className="bg-blue-50 border-blue-200">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-xs text-blue-900">
                  {mode === "create"
                    ? <>Dépose le contrat de travail — notre IA détecte automatiquement <strong>identité, poste, salaire, convention collective, période d'essai, coefficient</strong>. Tu pourras tout vérifier et corriger avant validation.</>
                    : <>Dépose un nouveau contrat pour cette fiche existante — l'IA remplira uniquement les champs actuellement vides <strong>sans écraser</strong> ce que tu as déjà saisi.</>}
                </AlertDescription>
              </Alert>
              <FileDrop file={file} onPick={setFile} />
              {mode === "enrich" && (
                <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                  <input id="overwrite-existing" name="overwrite_existing" type="checkbox" checked={overwriteExisting}
                    onChange={(e) => setOverwriteExisting(e.target.checked)}
                    data-testid="overwrite-existing" />
                  Écraser les champs déjà remplis dans la fiche (par défaut : seulement les vides).
                </label>
              )}
            </div>
          )}

          {step === "preview" && mode === "create" && result && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Extraction IA · {result.source_filename}</span>
                <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-700">
                  Confiance {Math.round((result.confidence || 0) * 100)}%
                </Badge>
              </div>

              {hasDuplicates && (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="w-4 h-4 text-amber-700" />
                  <AlertDescription className="text-xs text-amber-900">
                    <div className="font-semibold mb-1">
                      Doublon(s) potentiel(s) détecté(s)
                    </div>
                    <div className="space-y-1 mb-2">
                      {result.duplicates.map((d) => (
                        <label key={d.id}
                          className="flex items-center gap-2 p-2 bg-white rounded border border-amber-200 cursor-pointer">
                          <input type="radio" name="dup-target"
                            checked={targetEmployeeId === d.id && duplicateAction === "merge_into"}
                            onChange={() => { setDuplicateAction("merge_into"); setTargetEmployeeId(d.id); }}
                            data-testid={`dup-radio-${d.id}`} />
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">
                              {d.first_name} {d.last_name}
                            </div>
                            <div className="text-[11px] text-slate-500">
                              {d.job_title || "—"} · match sur {d.match_reason === "ss_number" ? "n° sécu" : "nom"}
                            </div>
                          </div>
                        </label>
                      ))}
                      <label className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200 cursor-pointer">
                        <input type="radio" name="dup-target"
                          checked={duplicateAction === "create_new" || !duplicateAction}
                          onChange={() => { setDuplicateAction("create_new"); setTargetEmployeeId(null); }}
                          data-testid="dup-create-new" />
                        <div className="flex items-center gap-1.5 text-slate-700 text-sm">
                          <UserPlus className="w-3.5 h-3.5" />
                          Créer un nouveau salarié quand même (homonyme)
                        </div>
                      </label>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <Card>
                <CardContent className="pt-4 space-y-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                    Identité
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldRow label="Prénom" required>
                      <Input value={empForm.first_name || ""}
                        onChange={(e) => setEmpForm(p => ({ ...p, first_name: e.target.value }))}
                        data-testid="preview-first-name" />
                    </FieldRow>
                    <FieldRow label="Nom" required>
                      <Input value={empForm.last_name || ""}
                        onChange={(e) => setEmpForm(p => ({ ...p, last_name: e.target.value }))}
                        data-testid="preview-last-name" />
                    </FieldRow>
                    <FieldRow label="Date de naissance">
                      <Input type="date" value={empForm.birth_date || ""}
                        onChange={(e) => setEmpForm(p => ({ ...p, birth_date: e.target.value }))} />
                    </FieldRow>
                    <FieldRow label="Lieu de naissance">
                      <Input value={empForm.birth_place || ""}
                        onChange={(e) => setEmpForm(p => ({ ...p, birth_place: e.target.value }))} />
                    </FieldRow>
                    <FieldRow label="N° Sécurité Sociale">
                      <Input value={empForm.ss_number || ""}
                        onChange={(e) => setEmpForm(p => ({ ...p, ss_number: e.target.value }))}
                        className="font-mono text-xs" data-testid="preview-ss" />
                    </FieldRow>
                    <FieldRow label="Nationalité">
                      <Input value={empForm.nationality || ""}
                        onChange={(e) => setEmpForm(p => ({ ...p, nationality: e.target.value }))} />
                    </FieldRow>
                    <FieldRow label="Email">
                      <Input type="email" value={empForm.email || ""}
                        onChange={(e) => setEmpForm(p => ({ ...p, email: e.target.value }))} />
                    </FieldRow>
                    <FieldRow label="Téléphone">
                      <Input value={empForm.phone || ""}
                        onChange={(e) => setEmpForm(p => ({ ...p, phone: e.target.value }))} />
                    </FieldRow>
                    <FieldRow label="Situation familiale">
                      <Select value={empForm.family_status || ""}
                        onValueChange={(v) => setEmpForm(p => ({ ...p, family_status: v }))}>
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {FAMILY_STATUS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </FieldRow>
                    <FieldRow label="Nombre d'enfants">
                      <Input type="number" min="0" value={empForm.children_count ?? ""}
                        onChange={(e) => setEmpForm(p => ({ ...p, children_count: e.target.value === "" ? null : parseInt(e.target.value, 10) }))} />
                    </FieldRow>
                  </div>

                  {(empForm.address || {}).street !== undefined && (
                    <>
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium pt-2 border-t border-slate-100">
                        Adresse
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <FieldRow label="Rue">
                          <Input value={empForm.address?.street || ""}
                            onChange={(e) => setEmpForm(p => ({ ...p, address: { ...(p.address || {}), street: e.target.value } }))} />
                        </FieldRow>
                        <FieldRow label="Code postal">
                          <Input value={empForm.address?.postal_code || ""}
                            onChange={(e) => setEmpForm(p => ({ ...p, address: { ...(p.address || {}), postal_code: e.target.value } }))} />
                        </FieldRow>
                        <FieldRow label="Ville">
                          <Input value={empForm.address?.city || ""}
                            onChange={(e) => setEmpForm(p => ({ ...p, address: { ...(p.address || {}), city: e.target.value } }))} />
                        </FieldRow>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4 space-y-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                    Poste & catégorie
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldRow label="Intitulé du poste">
                      <Input value={empForm.job_title || ""}
                        onChange={(e) => setEmpForm(p => ({ ...p, job_title: e.target.value }))}
                        data-testid="preview-job-title" />
                    </FieldRow>
                    <FieldRow label="Service / département">
                      <Input value={empForm.department || ""}
                        onChange={(e) => setEmpForm(p => ({ ...p, department: e.target.value }))} />
                    </FieldRow>
                    <FieldRow label="Catégorie">
                      <Select value={empForm.category || ""}
                        onValueChange={(v) => setEmpForm(p => ({ ...p, category: v }))}>
                        <SelectTrigger data-testid="preview-category"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </FieldRow>
                    <FieldRow label="Temps de travail">
                      <Select value={empForm.work_time_type || ""}
                        onValueChange={(v) => setEmpForm(p => ({ ...p, work_time_type: v }))}>
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {WORK_TIME.map(w => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </FieldRow>
                    <FieldRow label="Heures hebdo">
                      <Input type="number" step="0.5" value={empForm.hours_per_week ?? ""}
                        onChange={(e) => setEmpForm(p => ({ ...p, hours_per_week: e.target.value === "" ? null : parseFloat(e.target.value) }))} />
                    </FieldRow>
                    <FieldRow label="Date d'embauche">
                      <Input type="date" value={empForm.hire_date || ""}
                        onChange={(e) => setEmpForm(p => ({ ...p, hire_date: e.target.value }))} />
                    </FieldRow>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4 space-y-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                    Contrat
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldRow label="Type" required>
                      <Select value={ctrForm.contract_type || ""}
                        onValueChange={(v) => setCtrForm(p => ({ ...p, contract_type: v }))}>
                        <SelectTrigger data-testid="preview-contract-type"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {CONTRACT_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </FieldRow>
                    <FieldRow label="Date de début" required>
                      <Input type="date" value={ctrForm.start_date || ""}
                        onChange={(e) => setCtrForm(p => ({ ...p, start_date: e.target.value }))} />
                    </FieldRow>
                    <FieldRow label="Date de fin (CDD)">
                      <Input type="date" value={ctrForm.end_date || ""}
                        onChange={(e) => setCtrForm(p => ({ ...p, end_date: e.target.value }))} />
                    </FieldRow>
                    <FieldRow label="Fin de période d'essai">
                      <Input type="date" value={ctrForm.probation_end_date || ""}
                        onChange={(e) => setCtrForm(p => ({ ...p, probation_end_date: e.target.value }))} />
                    </FieldRow>
                    <FieldRow label="Salaire brut annuel (€)">
                      <Input type="number" step="0.01" value={ctrForm.gross_annual_salary ?? ""}
                        onChange={(e) => setCtrForm(p => ({ ...p, gross_annual_salary: e.target.value === "" ? null : parseFloat(e.target.value) }))}
                        data-testid="preview-annual-salary" />
                    </FieldRow>
                    <FieldRow label="Salaire brut mensuel (€)">
                      <Input type="number" step="0.01" value={ctrForm.gross_monthly_salary ?? ""}
                        onChange={(e) => setCtrForm(p => ({ ...p, gross_monthly_salary: e.target.value === "" ? null : parseFloat(e.target.value) }))} />
                    </FieldRow>
                    <FieldRow label="Convention collective">
                      <Input value={ctrForm.collective_agreement || ""}
                        onChange={(e) => setCtrForm(p => ({ ...p, collective_agreement: e.target.value }))} />
                    </FieldRow>
                    <FieldRow label="Coefficient / classification">
                      <Input value={ctrForm.coefficient || ""}
                        onChange={(e) => setCtrForm(p => ({ ...p, coefficient: e.target.value }))} />
                    </FieldRow>
                  </div>
                  <FieldRow label="Notes / clauses particulières">
                    <Textarea rows={2} value={ctrForm.notes || ""}
                      onChange={(e) => setCtrForm(p => ({ ...p, notes: e.target.value }))} />
                  </FieldRow>
                </CardContent>
              </Card>
            </div>
          )}

          {step === "done" && (
            <div className="py-8 text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-lg font-semibold text-slate-900">
                {mode === "enrich" ? "Fiche enrichie" : "Salarié créé"}
              </p>
              {mode === "enrich" && result?.fields_updated?.length > 0 && (
                <p className="text-sm text-slate-600">
                  {result.fields_updated.length} champ(s) rempli(s) automatiquement.
                </p>
              )}
              {mode === "create" && result?.applied?.employee_id && (
                <p className="text-sm text-slate-600">
                  {empForm.first_name} {empForm.last_name} · contrat + document PDF attachés.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="pt-3 border-t border-slate-100">
          {step === "upload" && (
            <>
              <Button variant="ghost" onClick={close}>Annuler</Button>
              <Button onClick={runDetect} disabled={!file || detecting}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="wizard-detect-btn">
                {detecting
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Sparkles className="w-4 h-4 mr-2" />}
                {mode === "enrich" ? "Analyser et enrichir" : "Analyser avec l'IA"}
              </Button>
            </>
          )}
          {step === "preview" && mode === "create" && (
            <>
              <Button variant="outline" onClick={() => setStep("upload")}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Retour
              </Button>
              <Button onClick={applyResult} disabled={applying}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="wizard-apply-btn">
                {applying
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : hasDuplicates && duplicateAction === "merge_into"
                    ? <UserCheck className="w-4 h-4 mr-2" />
                    : <UserPlus className="w-4 h-4 mr-2" />}
                {hasDuplicates && duplicateAction === "merge_into"
                  ? "Enrichir la fiche existante"
                  : "Créer le salarié"}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === "done" && (
            <Button onClick={close} className="w-full">Fermer</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepPills({ current, enrich }) {
  const steps = enrich ? ["upload", "done"] : STEPS;
  return (
    <div className="flex items-center gap-2 mt-2">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={cn(
            "w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold",
            steps.indexOf(current) >= i
              ? "bg-purple-600 text-white"
              : "bg-slate-200 text-slate-500",
          )}>{i + 1}</div>
          {i < steps.length - 1 && (
            <div className={cn(
              "h-0.5 w-8",
              steps.indexOf(current) > i ? "bg-purple-600" : "bg-slate-200",
            )} />
          )}
        </div>
      ))}
    </div>
  );
}
